"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/AddAccountModal/AddAccountModalContent";

import React from "react";
import { Icon } from "@ospweb/sdk-ui";
import { useMemoizedFn } from "ahooks";
import { cn } from "utils";
import type { PlaidImportModalResult } from "@/src/components/PlaidImportModal/types";

const I18n = I18nUtil.createScoped(Translations);

const PLAID_LOGO_MARK_SRC = "/assets/images/plaid/plaid-logo-white.webp";
const MODAL_DISMISS_DELAY_MS = 300;

export interface AddAccountModalContentProps {
  /** Fires after the drawer dismisses (Connect with Plaid path). */
  onConnect: () => void;
  /** Fires after the drawer dismisses (Send screenshot path). */
  onUpload: () => void;
  /** Closes the drawer without triggering any action callback. */
  onClose: (result: PlaidImportModalResult) => void;
}

export const AddAccountModalContent: React.FC<AddAccountModalContentProps> = ({
  onConnect,
  onUpload,
  onClose,
}) => {
  const handleConnect = useMemoizedFn(() => {
    onClose({ action: "close" });
    window.setTimeout(() => onConnect(), MODAL_DISMISS_DELAY_MS);
  });

  const handleUpload = useMemoizedFn(() => {
    onClose({ action: "close" });
    window.setTimeout(() => onUpload(), MODAL_DISMISS_DELAY_MS);
  });

  const handleClose = useMemoizedFn(() => {
    onClose({ action: "close" });
  });

  return (
    <div
      className="flex w-full flex-col bg-background_solid_neutral_secondary"
      data-testid="add-account-modal-content"
    >
      <div className="flex shrink-0 justify-center py-2 md:hidden">
        <span
          className="h-1 w-9 shrink-0 rounded-[2px] bg-background_white_overlay20"
          data-testid="add-account-modal-handle"
        />
      </div>
      <div className="relative flex shrink-0 items-center justify-center px-4 pb-2 pt-3 md:px-6 md:pt-6">
        <h2 className="title_xl text-content_primary">
          {I18n.t("Add account")}
        </h2>
        <button
          type="button"
          aria-label="Close"
          className="absolute right-[11px] top-3 z-10 flex size-10 items-center justify-center rounded-full bg-background_solid_neutral_primary shadow-[0px_2px_20px_0px_rgba(0,0,0,0.08)] md:right-6 md:top-6"
          data-testid="add-account-modal-close"
          onClick={handleClose}
        >
          <Icon name="close" size={20} className="text-content_primary" />
        </button>
      </div>
      <div className="flex flex-col gap-2 px-4 pb-4 pt-2 md:px-6">
        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-2xl bg-background_solid_neutral_primary p-3 text-left"
          data-testid="add-account-connect-plaid"
          onClick={handleConnect}
        >
          <span className="flex size-10 shrink-0 items-center justify-center rounded-2 bg-black">
            <img src={PLAID_LOGO_MARK_SRC} alt="Plaid" className="size-7" />
          </span>
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="title_m text-content_primary">
              {I18n.t("Connect with Plaid")}
            </span>
            <span className="body_s text-content_tertiary">
              {I18n.t("Auto-synced and always up to date")}
            </span>
          </span>
          <Icon
            name="right-arrow-alt"
            size={20}
            className="shrink-0 text-content_tertiary"
          />
        </button>

        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-2xl bg-background_solid_neutral_primary p-3 text-left"
          data-testid="add-account-upload-screenshot"
          onClick={handleUpload}
        >
          <span className="flex size-10 shrink-0 items-center justify-center rounded-2 bg-background_white_overlay5">
            <Icon
              name="cloud-upload"
              size={24}
              className="text-content_primary"
            />
          </span>
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="title_m text-content_primary">
              {I18n.t("Send Ed a screenshot")}
            </span>
            <span className="body_s text-content_tertiary">
              {I18n.t("I'll read your holdings from a photo")}
            </span>
          </span>
          <Icon
            name="right-arrow-alt"
            size={20}
            className="shrink-0 text-content_tertiary"
          />
        </button>
      </div>
      <div className={cn("px-4 pb-7 pt-2 md:px-6 md:pb-6")}>
        <p className="body_s text-center text-content_tertiary">
          {I18n.t(
            "Bank-level encryption. Read-only — I can't move your money.",
          )}
        </p>
      </div>
    </div>
  );
};
